import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * After the gallery is swapped from the Section Rendering response, scroll the carousel to the
 * variant's featured media. Slideshow initializes asynchronously (`#scroll` is set in a later task),
 * so we retry until `select` can run.
 *
 * @param {HTMLElement} mediaGallery
 * @param {string | number} featuredMediaId
 */
function syncSlideshowToFeaturedMedia(mediaGallery, featuredMediaId) {
  const idString = String(featuredMediaId);

  const applySelection = () => {
    const root = /** @type {HTMLElement & { refs?: { slideshow?: { select?: Function } } }} */ (mediaGallery);
    const slideshow = root.refs?.slideshow;
    if (!slideshow?.select) return;

    // Always select by slide-id so order matches slideshow refs (hidden slides, etc.) and we never rely on DOM index alone.
    slideshow.select({ id: idString }, undefined, { animate: false });
  };

  queueMicrotask(() => requestAnimationFrame(applySelection));
  [0, 50, 120, 250, 450].forEach((delayMs) => setTimeout(applySelection, delayMs));
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    // Listen on document so we never miss updates (some layouts break closest('.shopify-section')).
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateFromDocument, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdateFromDocument = (event) => {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker) return;
    const galleryProductId = this.dataset.productId;
    const pickerProductId = picker.dataset.productId;
    if (galleryProductId && pickerProductId && String(galleryProductId) !== String(pickerProductId)) {
      return;
    }
    this.#handleVariantUpdate(/** @type {import('@theme/events').VariantUpdateEvent} */ (event));
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data?.html;
    const eventProductId = event.detail.data?.productId;

    if (eventProductId && this.dataset.productId && String(eventProductId) !== String(this.dataset.productId)) {
      return;
    }

    const featuredMediaId = event.detail.resource?.featured_media?.id;

    if (!source) {
      if (featuredMediaId != null && featuredMediaId !== '') {
        syncSlideshowToFeaturedMedia(this, featuredMediaId);
      }
      return;
    }

    const newMediaGallery =
      source.querySelector('[data-testid="product-information-media"] media-gallery') ??
      source.querySelector('media-gallery');

    if (!newMediaGallery) {
      if (featuredMediaId != null && featuredMediaId !== '') {
        syncSlideshowToFeaturedMedia(this, featuredMediaId);
      }
      return;
    }

    this.replaceWith(newMediaGallery);

    if (featuredMediaId != null && featuredMediaId !== '') {
      syncSlideshowToFeaturedMedia(newMediaGallery, featuredMediaId);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
